import React, { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ChangeEvent, ReactNode, CSSProperties } from 'react';
import styled from 'styled-components';

type CustomOtpInputProps = {
  value: string;
  onChange: (otp: string) => void;
  numInputs?: number;
  separator?: ReactNode;
  isInputNum?: boolean;
  shouldAutoFocus?: boolean;
  inputStyle?: CSSProperties;
  inputClassName?: string;
  containerStyle?: CSSProperties;
  isInputSecure?: boolean;
};

const InputContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 2px;
  max-width: 100%;
  margin: 0 auto;
`;

const StyledInput = styled.input<{ $isFocused: boolean }>`
  width: 40px;
  height: 48px;
  border-radius: 4px;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 500;
  background: #FFFFFF;
  border: 1px solid ${({ $isFocused, theme }) => $isFocused ? theme.colors.secondary : '#E0E0E0'};
  outline: none;
  padding: 0;
  transition: all 0.2s ease-in-out;
  
  &:focus {
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.lightGray};
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const CustomOtpInput: React.FC<CustomOtpInputProps> = ({
  inputClassName,
  value = '',
  onChange,
  numInputs = 6,
  separator,
  isInputNum = true,
  shouldAutoFocus = true,
  isInputSecure = false,
}) => {
  const [activeInput, setActiveInput] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  
  // Initialize the refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, numInputs);
    while (inputRefs.current.length < numInputs) {
      inputRefs.current.push(null);
    }
  }, [numInputs]);

  // Focus on the first input on mount if shouldAutoFocus is true
  useEffect(() => {
    if (shouldAutoFocus && inputRefs.current[0]) {
      inputRefs.current[0]?.focus();
    }
  }, [shouldAutoFocus]);

  // Handle input change
  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const { value: inputValue } = e.target;
    
    // Only allow numbers if isInputNum is true
    if (isInputNum && !/^\d*$/.test(inputValue) && inputValue !== '') {
      return;
    }

    // Update OTP value
    const newOtp = value.split('');
    newOtp[index] = inputValue.substring(inputValue.length - 1); // Get the last character
    const otpValue = newOtp.join('');
    
    // Update parent component
    onChange(otpValue);

    // Move to next input if there's a value and not the last input
    if (inputValue && index < numInputs - 1) {
      setActiveInput(index + 1);
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle key down events for navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      
      // If current input is empty, move to previous input and clear it
      if (!value[index] && index > 0) {
        const newOtp = value.split('');
        newOtp[index - 1] = '';
        onChange(newOtp.join(''));
        setActiveInput(index - 1);
        inputRefs.current[index - 1]?.focus();
      } else if (value[index]) {
        // Clear current input
        const newOtp = value.split('');
        newOtp[index] = '';
        onChange(newOtp.join(''));
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      // Move to previous input
      setActiveInput(index - 1);
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < numInputs - 1) {
      // Move to next input
      setActiveInput(index + 1);
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === ' ' || e.key === 'Spacebar') {
      // Prevent spacebar from scrolling the page
      e.preventDefault();
    }
  };

  // Handle paste event
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    
    if (isInputNum && !/^\d*$/.test(pastedData)) {
      return;
    }

    // Get remaining inputs that can be filled
    const remainingLength = numInputs - index;
    const valueToPaste = pastedData.substring(0, remainingLength);
    
    if (valueToPaste) {
      const newOtp = value.split('');
      for (let i = 0; i < valueToPaste.length; i++) {
        if (index + i < numInputs) {
          newOtp[index + i] = valueToPaste[i];
        }
      }
      
      const newValue = newOtp.join('');
      onChange(newValue);
      
      // Move focus to the next empty input or the last input
      const nextIndex = Math.min(index + valueToPaste.length, numInputs - 1);
      setActiveInput(nextIndex);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  // Handle input click
  const handleClick = (index: number) => {
    setActiveInput(index);
    inputRefs.current[index]?.focus();
  };

  // Generate input fields
  const inputs = [];
  for (let i = 0; i < numInputs; i++) {
    inputs.push(
      <React.Fragment key={i}>
        <StyledInput
          ref={el => {
            if (el) {
              inputRefs.current[i] = el;
            }
          }}
          type={isInputSecure ? 'password' : 'text'}
          inputMode={isInputNum ? 'numeric' : 'text'}
          maxLength={1}
          value={value[i] || ''}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onPaste={(e) => handlePaste(e, i)}
          onClick={() => handleClick(i)}
          $isFocused={activeInput === i}
          className={inputClassName}
          autoComplete="one-time-code"
          aria-label={`Digit ${i + 1} of ${numInputs}`}
        />
        {i < numInputs - 1 && separator}
      </React.Fragment>
    );
  }

  return (
    <InputContainer>
      {inputs}
    </InputContainer>
  );
};

export default CustomOtpInput;
